import React from 'react';

import { useLocation } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import AttractionsPage from '~/components/Attractions/AttractionsPage';
import SectionNavigation from '~/components/Common/Elements/SectionNavigation';
import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';

import { ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR } from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import canShowTab from '~/utils/canShowTab';

import CarouselMetricsTab from './Content/CarouselMetrics/CarouselMetricsTab';
import Collections from './Content/Collections/Collections';
import ContentTagsTab from './Content/ContentTagPerformance/ContentTagsTab';
import RegionCuration from './Content/RegionCuration/RegionCuration';
import SyncLogs from './Content/SyncLogs/SyncLogs';
import SyncOffers from './Content/SyncOffers/SyncOffers';
import { ExperiencesContent } from './ExperiencesContent';

export type FieldValueParams = {
  key: string;
  fieldValue: string;
  range?: 'min' | 'max';
};

export type Toggle = {
  [key: string]: boolean;
};

type CurationTab =
  | 'region-curation'
  | 'experiences-curation'
  | 'sync-offers'
  | 'collections'
  | 'carousel-metrics'
  | 'content-tag-performance'
  | 'sync-logs'
  | 'attractions';

const VALID_TABS: Array<CurationTab> = [
  'experiences-curation',
  'region-curation',
  'sync-offers',
  'collections',
  'sync-logs',
  'carousel-metrics',
  'content-tag-performance',
  'attractions',
];

export default function Experiences() {
  const { replace } = useAppNavigation();
  const location = useLocation();
  const { user } = useCurrentUser();
  const showCarouselMetrics = canShowTab(user, [ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR]);
  const showContentTagPerformance = canShowTab(user, [ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR]);

  const params = new URLSearchParams(location.search);
  const tabParam = params.get('tab') as CurationTab | null;
  const tab: CurationTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'experiences-curation';

  const handleTabChange = (_: React.SyntheticEvent, value: CurationTab) => {
    if (value === 'experiences-curation') {
      replace('/experiences');
    } else {
      replace(`/experiences?tab=${value}`);
    }
  };

  return (
    <Container>
      <SectionNavigationLayout
        navigation={
          <SectionNavigation
            label="Experiences sections"
            currentTab={tab}
            groups={[
              {
                items: [
                  {
                    id: 'experiences-curation',
                    label: 'Experiences Curation',

                    href: '/experiences?tab=experiences-curation',
                  },
                  {
                    id: 'region-curation',
                    label: 'In Flow Region Curation',

                    href: '/experiences?tab=region-curation',
                  },
                  {
                    id: 'sync-offers',
                    label: 'Sync Offers',

                    href: '/experiences?tab=sync-offers',
                  },
                  {
                    id: 'collections',
                    label: 'Collections',

                    href: '/experiences?tab=collections',
                  },
                  { id: 'sync-logs', label: 'Sync Logs', href: '/experiences?tab=sync-logs' },
                  {
                    id: 'carousel-metrics',
                    label: 'Carousel Metrics',

                    href: '/experiences?tab=carousel-metrics',
                  },
                  {
                    id: 'content-tag-performance',
                    label: 'Content Tag',

                    href: '/experiences?tab=content-tag-performance',
                  },
                  {
                    id: 'attractions',
                    label: 'Attractions',

                    href: '/experiences?tab=attractions',
                  },
                ].filter(
                  (item) =>
                    (item.id !== 'carousel-metrics' || showCarouselMetrics) &&
                    (item.id !== 'content-tag-performance' || showContentTagPerformance),
                ),
              },
            ]}
            onSelect={(event, id) => {
              event.preventDefault();
              handleTabChange(event, id as CurationTab);
            }}
          />
        }
      >
        <Box mt={4}>
          {tab === 'experiences-curation' && <ExperiencesContent />}
          {tab === 'region-curation' && <RegionCuration />}
          {tab === 'sync-offers' && <SyncOffers />}
          {tab === 'collections' && <Collections />}
          {tab === 'sync-logs' && <SyncLogs />}
          {tab === 'carousel-metrics' && showCarouselMetrics && <CarouselMetricsTab />}
          {tab === 'content-tag-performance' && showContentTagPerformance && <ContentTagsTab />}
          {tab === 'attractions' && <AttractionsPage />}
        </Box>
      </SectionNavigationLayout>
    </Container>
  );
}
